import React from "react";
import PropTypes from "prop-types";
import { Tooltip, Chip } from "@mui/material";
import VehicleIcon from "../../assets/svg/layout/car.svg";
import EventIcon from "../../assets/svg/layout/car.svg";
import CapacityIcon from "../../assets/svg/layout/clock.svg";
import RouteIcon from "@mui/icons-material/Route";
import RouteSelector from "../ui/dropdawn/RouteSelector";
import "../../assets/scss/layout/SchedulerHeader.scss";

const SchedulerHeader = ({
  theme,
  operationName,
  onVehiclesClick,
  onEventsClick,
  onCapacityClick,
  onRoutesClick,
  showPredictions,
  onTogglePredictions,
  capacityStatus,
  routes = [],
  selectedRouteId,
  onRouteChange,
  isLoadingRoutes = false
}) => {
  
  // Determinar si las operaciones están habilitadas
  const isOperationsEnabled = () => {
    // Si solo hay una ruta, siempre está habilitado
    if (routes.length === 1) return true;
    // Si hay múltiples rutas, necesita selección
    if (routes.length > 1) return !!selectedRouteId;
    // Si no hay rutas, deshabilitado
    return false;
  };

  const operationsEnabled = isOperationsEnabled();

  // Mensaje de tooltip cuando está deshabilitado
  const getDisabledTooltip = () => {
    if (routes.length === 0) return "No hay rutas configuradas para esta operación";
    if (routes.length > 1 && !selectedRouteId) return "Selecciona una ruta para continuar";
    return "";
  };

  const disabledTooltip = getDisabledTooltip();

  return (
    <div className={`GM__${theme}-scheduler-header`}>
      <div className={`GM__${theme}-header-left`}>
        {/* Botón Vehículos - Condicional */}
        {operationsEnabled ? (
          <button
            className={`GM__${theme}-header-button`}
            onClick={onVehiclesClick}
          >
            <span>Vehículos</span>
            <img
              src={VehicleIcon}
              alt="Vehículos"
              className={`GM__${theme}-button-icon`}
            />
          </button>
        ) : (
          <Tooltip title={disabledTooltip} arrow>
            <div>
              <button
                className={`GM__${theme}-header-button disabled`}
                disabled
                style={{ 
                  opacity: 0.5, 
                  cursor: 'not-allowed',
                  pointerEvents: 'none'
                }}
              >
                <span>Vehículos</span>
                <img
                  src={VehicleIcon}
                  alt="Vehículos"
                  className={`GM__${theme}-button-icon`}
                />
              </button>
            </div>
          </Tooltip>
        )}
        
        {/* Botón Eventos - Condicional */}
        {operationsEnabled ? (
          <button 
            className={`GM__${theme}-header-button`} 
            onClick={onEventsClick}
          >
            <span>Eventos</span>
            <img
              src={EventIcon}
              alt="Eventos"
              className={`GM__${theme}-button-icon`}
            />
          </button>
        ) : (
          <Tooltip title={disabledTooltip} arrow>
            <div>
              <button
                className={`GM__${theme}-header-button disabled`}
                disabled
                style={{ 
                  opacity: 0.5, 
                  cursor: 'not-allowed',
                  pointerEvents: 'none'
                }}
              >
                <span>Eventos</span>
                <img
                  src={EventIcon}
                  alt="Eventos"
                  className={`GM__${theme}-button-icon`}
                />
              </button>
            </div>
          </Tooltip>
        )}
        
        {/* Botón Capacidad - Condicional */}
        {operationsEnabled ? (
          <button
            className={`GM__${theme}-header-button`}
            onClick={onCapacityClick}
          >
            <span>Capacidad</span>
            <img
              src={CapacityIcon}
              alt="Capacidad"
              className={`GM__${theme}-button-icon`}
            />
          </button>
        ) : (
          <Tooltip title={disabledTooltip} arrow>
            <div>
              <button
                className={`GM__${theme}-header-button disabled`}
                disabled
                style={{ 
                  opacity: 0.5, 
                  cursor: 'not-allowed',
                  pointerEvents: 'none'
                }}
              >
                <span>Capacidad</span>
                <img
                  src={CapacityIcon}
                  alt="Capacidad"
                  className={`GM__${theme}-button-icon`}
                />
              </button>
            </div>
          </Tooltip>
        )}
        
        {/* Botón Rutas - Siempre visible */}
        <button
          className={`GM__${theme}-header-button`}
          onClick={onRoutesClick}
        >
          <span>Rutas</span>
          <RouteIcon className={`GM__${theme}-button-icon-mui`} />
        </button>
      </div>

      <div className={`GM__${theme}-header-center`}>
        {/* Selector de rutas */}
        {routes.length > 0 && (
          <RouteSelector
            routes={routes}
            selectedRouteId={selectedRouteId}
            onRouteChange={onRouteChange}
            isLoading={isLoadingRoutes}
            showAllOption={false} // Cambiado a false para forzar selección
          />
        )}
        
        {/* Mensaje de ayuda cuando no hay ruta seleccionada */}
        {routes.length > 1 && !selectedRouteId && (
          <div className={`GM__${theme}-route-warning`}>
            <Chip
              label="⚠️ Selecciona una ruta para continuar"
              color="warning"
              variant="outlined"
              size="small"
            />
          </div>
        )}
      </div>

      <div className={`GM__${theme}-header-right`}>
        <div className={`GM__${theme}-prediction-toggle`}>
          <label className={`GM__${theme}-toggle-label`}>
            <input
              type="checkbox"
              checked={showPredictions}
              onChange={onTogglePredictions}
              className={`GM__${theme}-toggle-input`}
            />
            <span className={`GM__${theme}-toggle-text`}>
              {showPredictions
                ? "Ocultar predicciones"
                : "Mostrar predicciones"}
            </span>
          </label>
        </div>
        
        {/* Indicador de capacidad mensual - Solo si hay ruta seleccionada */}
        {operationsEnabled &&
          capacityStatus &&
          capacityStatus.data &&
          capacityStatus.data.length > 0 &&
          (() => {
            const totalScheduled = capacityStatus.data.reduce(
              (sum, day) => sum + (day.scheduledCount || 0),
              0
            );
            const totalPlannedCapacity = capacityStatus.data.reduce(
              (sum, day) => sum + (day.plannedCapacity || 0),
              0
            );

            const utilizationPercentage =
              totalPlannedCapacity > 0
                ? (totalScheduled / totalPlannedCapacity) * 100
                : 0;

            const roundedPercentage = Math.round(utilizationPercentage);

            const chipColor =
              utilizationPercentage > 100
                ? "error"
                : utilizationPercentage > 80
                ? "warning"
                : "success";

            return (
              <div className={`GM__${theme}-capacity-summary`}>
                <Chip
                  label={`Capacidad del mes: ${roundedPercentage}%`}
                  color={chipColor}
                  size="small"
                />
              </div>
            );
          })()}
      </div>
    </div>
  );
};

SchedulerHeader.propTypes = {
  theme: PropTypes.string.isRequired,
  operationName: PropTypes.string,
  onVehiclesClick: PropTypes.func,
  onEventsClick: PropTypes.func,
  onCapacityClick: PropTypes.func,
  onRoutesClick: PropTypes.func,
  showPredictions: PropTypes.bool,
  onTogglePredictions: PropTypes.func,
  capacityStatus: PropTypes.object,
  routes: PropTypes.array,
  selectedRouteId: PropTypes.string,
  onRouteChange: PropTypes.func,
  isLoadingRoutes: PropTypes.bool
};

export default SchedulerHeader;